/* ──────────────────────────────────────────────────────────────────────────
   dz-viz-kit.jsx — shared NATIVE motion + infographic + interaction kit.

   The repo has NO animation library. Everything here animates with
   IntersectionObserver + `.is-in-view` CSS and (for the scroll-story) a
   passive scroll listener + rAF. No GSAP.

   PRERENDER-SAFE CONTRACT (critical): every primitive renders its FINAL
   static state by default. Motion classes (`is-in-view`, `is-scrollstory-active`)
   are added ONLY on scroll-in — so prerender's no-scroll snapshot and no-JS
   users always keep the full baseline. Reduced-motion short-circuits to static.

   Exposes window.DZViz = { usePrefersReducedMotion, useReveal, useScrollStory,
                            BeforeAfterSlider, SegmentedControl }
   ────────────────────────────────────────────────────────────────────────── */
/* global React */
(function () {
  'use strict';
  const { useRef, useEffect, useState, useCallback, useId } = React;
  const REDUCE = '(prefers-reduced-motion: reduce)';
  const prefersReduced = () => !!(window.matchMedia && window.matchMedia(REDUCE).matches);

  /* live reduced-motion flag */
  function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
      const mq = window.matchMedia(REDUCE);
      const on = () => setReduced(mq.matches);
      on();
      mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
      return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
    }, []);
    return reduced;
  }

  /* One-shot reveal: adds `.is-in-view` when the element scrolls into view.
     Resting CSS = final state; `.is-in-view` plays a media-gated entrance.
     Not added during prerender (no scroll) → snapshot keeps the final state. */
  function useReveal(threshold) {
    const ref = useRef(null);
    const th = threshold == null ? 0.18 : threshold;
    useEffect(() => {
      const el = ref.current;
      if (!el || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { el.classList.add('is-in-view'); io.disconnect(); }
        });
      }, { threshold: th });
      io.observe(el);
      return () => io.disconnect();
    }, [th]);
    return ref;
  }

  /* Sticky scroll-story driver. Maps scroll progress THROUGH a tall section to a
     step index 0..stepCount-1 and calls onStep(step). Adds `is-scrollstory-active`
     only once the section enters view (motion only). Passive listener + rAF,
     one rect read per frame, transform/opacity work is the consumer's job.
     Reduced-motion / no-JS / prerender → never activates → static baseline. */
  function useScrollStory(stepCount, onStep) {
    const ref = useRef(null);
    const cb = useRef(onStep);
    cb.current = onStep;
    useEffect(() => {
      const el = ref.current;
      if (!el || !('IntersectionObserver' in window) || stepCount < 1) return;
      if (prefersReduced()) return; // static baseline (all layers shown)
      let active = false, ticking = false, last = -1;
      const compute = () => {
        ticking = false;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const total = rect.height - vh;            // scrollable distance while pinned
        let p = total > 8 ? (0 - rect.top) / total : (rect.top < vh * 0.5 ? 1 : 0);
        p = p < 0 ? 0 : p > 1 ? 1 : p;
        const step = Math.min(stepCount - 1, Math.floor(p * stepCount));
        if (step !== last) { last = step; el.dataset.step = String(step); if (cb.current) cb.current(step); }
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute); } };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !active) {
            active = true;
            el.classList.add('is-scrollstory-active');
            window.addEventListener('scroll', onScroll, { passive: true });
            compute();
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -20% 0px' });
      io.observe(el);
      return () => { io.disconnect(); window.removeEventListener('scroll', onScroll); };
    }, [stepCount]);
    return ref;
  }

  /* Draggable / keyboard before-after comparison.
     SSR baseline: the "after" pane clipped at 50% — both sides real content in
     the DOM. A range input (role=slider) drives `--dz-ba-pos`; pointer drag too. */
  function BeforeAfterSlider(props) {
    const { before, after, beforeLabel, afterLabel, ariaLabel } = props;
    const [pos, setPos] = useState(50);
    const wrapRef = useRef(null);
    const dragging = useRef(false);
    const id = useId();

    const setFromClientX = useCallback((clientX) => {
      const el = wrapRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      let p = ((clientX - r.left) / r.width) * 100;
      p = p < 0 ? 0 : p > 100 ? 100 : p;
      setPos(Math.round(p));
    }, []);

    useEffect(() => {
      const move = (ev) => { if (dragging.current) setFromClientX(ev.touches ? ev.touches[0].clientX : ev.clientX); };
      const up = () => { dragging.current = false; };
      window.addEventListener('pointermove', move, { passive: true });
      window.addEventListener('pointerup', up, { passive: true });
      window.addEventListener('touchmove', move, { passive: true });
      window.addEventListener('touchend', up, { passive: true });
      // touch-action:pan-y means a vertical page-scroll started on the slider
      // fires *cancel — without these the slider keeps scrubbing under the pan.
      window.addEventListener('pointercancel', up, { passive: true });
      window.addEventListener('touchcancel', up, { passive: true });
      return () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('touchmove', move);
        window.removeEventListener('touchend', up);
        window.removeEventListener('pointercancel', up);
        window.removeEventListener('touchcancel', up);
      };
    }, [setFromClientX]);

    return (
      <div className="dz-viz-ba" ref={wrapRef} style={{ '--dz-ba-pos': pos + '%' }}
           onPointerDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}>
        <div className="dz-viz-ba__pane dz-viz-ba__pane--before" aria-hidden="true">
          {beforeLabel && <span className="dz-viz-ba__tag dz-viz-ba__tag--before">{beforeLabel}</span>}
          {before}
        </div>
        <div className="dz-viz-ba__pane dz-viz-ba__pane--after" aria-hidden="true">
          {afterLabel && <span className="dz-viz-ba__tag dz-viz-ba__tag--after">{afterLabel}</span>}
          {after}
        </div>
        <div className="dz-viz-ba__divider" style={{ left: pos + '%' }} aria-hidden="true">
          <span className="dz-viz-ba__handle">
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M7 5L3 10l4 5M13 5l4 5-4 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
        <label className="dz-viz-ba__sr" htmlFor={id}>{ariaLabel || 'Reveal comparison'}</label>
        <input id={id} className="dz-viz-ba__range" type="range" min="0" max="100" value={pos}
               aria-label={ariaLabel || 'Reveal comparison'}
               aria-valuetext={pos + '% — ' + (afterLabel || 'after')}
               onChange={(e) => setPos(Number(e.target.value))} />
      </div>
    );
  }

  /* Accessible segmented control (radiogroup). SSR shows the default value's
     panel via the consumer; this only manages selection + keyboard. */
  function SegmentedControl(props) {
    const { options, value, onChange, ariaLabel } = props;
    const idx = Math.max(0, options.findIndex((o) => o.value === value));
    const onKey = (e) => {
      let n = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (idx + 1) % options.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (idx - 1 + options.length) % options.length;
      if (n != null) { e.preventDefault(); onChange(options[n].value); }
    };
    return (
      <div className="dz-viz-seg" role="radiogroup" aria-label={ariaLabel} onKeyDown={onKey}>
        <span className="dz-viz-seg__thumb" style={{ '--dz-seg-n': options.length, '--dz-seg-i': idx }} aria-hidden="true" />
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}
                  tabIndex={o.value === value ? 0 : -1}
                  className={'dz-viz-seg__opt' + (o.value === value ? ' is-active' : '')}
                  onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }

  window.DZViz = { usePrefersReducedMotion, useReveal, useScrollStory, BeforeAfterSlider, SegmentedControl };
})();
